import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface Tier {
  id: string;
  name: string;
  slug: string;
  monthly_characters: number;
  price_monthly: number;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  is_active: boolean;
}

export function Tiers() {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [editValues, setEditValues] = useState<Partial<Tier>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    setLoading(true);
    const result = await api.getTiers();
    if (result.success && result.data) {
      setTiers(result.data);
    } else {
      setError(result.error?.message || 'Failed to load tiers');
    }
    setLoading(false);
  };

  const handleEdit = (tier: Tier) => {
    setEditingTier(tier);
    setEditValues({
      stripe_price_id_monthly: tier.stripe_price_id_monthly || '',
      stripe_price_id_yearly: tier.stripe_price_id_yearly || '',
    });
    setMessage(null);
  };

  const handleSave = async () => {
    if (!editingTier) return;

    setSaving(true);
    const result = await api.updateTier(editingTier.id, editValues);
    if (result.success) {
      setMessage({ type: 'success', text: 'Tier updated successfully' });
      setEditingTier(null);
      await loadTiers();
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to update tier' });
    }
    setSaving(false);
  };

  const formatCharacters = (n: number) => {
    if (n >= 1000000) return (n / 1000000).toFixed(0) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
    return n.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600">{error}</p>
        <button onClick={loadTiers} className="btn btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Tiers</h1>
          <p className="text-gray-600 mt-1">Configure pricing tiers and Stripe price IDs</p>
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tiers.map((tier) => (
          <div key={tier.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{tier.name}</h3>
                <p className="text-sm text-gray-500">{tier.slug}</p>
              </div>
              {tier.is_active ? (
                <span className="badge badge-success">Active</span>
              ) : (
                <span className="badge badge-gray">Inactive</span>
              )}
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Monthly Price</span>
                <span className="font-medium">${tier.price_monthly / 100}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Characters/Month</span>
                <span className="font-medium">{formatCharacters(tier.monthly_characters)}</span>
              </div>
            </div>

            {editingTier?.id === tier.id ? (
              <div className="space-y-4 border-t pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Price ID
                  </label>
                  <input
                    type="text"
                    value={editValues.stripe_price_id_monthly || ''}
                    onChange={(e) => setEditValues({ ...editValues, stripe_price_id_monthly: e.target.value })}
                    placeholder="price_..."
                    className="input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yearly Price ID
                  </label>
                  <input
                    type="text"
                    value={editValues.stripe_price_id_yearly || ''}
                    onChange={(e) => setEditValues({ ...editValues, stripe_price_id_yearly: e.target.value })}
                    placeholder="price_..."
                    className="input text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn btn-primary flex-1"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingTier(null)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-t pt-4">
                <div className="space-y-2 mb-4">
                  <div>
                    <span className="text-xs text-gray-500">Monthly Price ID</span>
                    <p className="text-sm font-mono truncate">
                      {tier.stripe_price_id_monthly || <span className="text-yellow-600">Not set</span>}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Yearly Price ID</span>
                    <p className="text-sm font-mono truncate">
                      {tier.stripe_price_id_yearly || <span className="text-yellow-600">Not set</span>}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(tier)}
                  className="btn btn-outline w-full"
                >
                  Edit Price IDs
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="card mt-6 bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Stripe Price IDs</p>
            <p>
              To enable subscriptions, create products and prices in your Stripe dashboard,
              then paste the price IDs here. Price IDs start with <code className="bg-blue-100 px-1 rounded">price_</code>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
